import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, EmptyState, Spinner, StandaloneScreen } from '../../components/app2';
import api from '../../utils/api';
import { authService } from '../../utils/authService';

/**
 * Aterrizaje del magic-link (registro por el asistente de WhatsApp).
 *
 * Llega como /entrar?uid=<id>&token=<raw>. Canjea el token single-use por una
 * sesión (POST /api/auth/login-with-token), guarda el JWT igual que el login
 * normal y redirige: si debe cambiar contraseña → /mandatory-password-change;
 * si no → a elegir su plan (donde vive la prueba de 15 días).
 *
 * Rediseño jul-2026: pantalla suelta sobre StandaloneScreen (tokens, fuentes y
 * tema de la app). La lógica de canje no cambia.
 */
const Entrar = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState('loading'); // loading | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const uid = searchParams.get('uid');
    const token = searchParams.get('token');

    if (!uid || !token) {
      setErrorMsg('El enlace está incompleto. Solicita uno nuevo o inicia sesión con tu correo.');
      setState('error');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await api.post('/api/auth/login-with-token', { userId: Number(uid), token });
        if (cancelled) return;
        const { jwt, user } = res?.data || {};
        if (!jwt) throw new Error('sin jwt');

        // Normaliza y guarda la sesión con el mismo shape que el login normal
        // (incluye el `role` mapeado que los guards de ruta exigen).
        const { mustChangePassword } = authService.establishSessionFromToken(jwt, user);

        // Debe definir su contraseña (cuenta creada sin contraseña propia).
        if (mustChangePassword) {
          navigate('/mandatory-password-change', { replace: true });
        } else {
          navigate('/app/account/plans/upgrade', { replace: true });
        }
      } catch (err) {
        if (cancelled) return;
        const msg =
          err?.response?.data?.error?.message ||
          'El enlace no es válido o ya expiró. Solicita uno nuevo o inicia sesión con tu correo.';
        setErrorMsg(msg);
        setState('error');
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StandaloneScreen maxWidth={480} title="Entrando a tu cuenta — PaySlip">
      {state === 'loading' ? (
        <Card style={{ textAlign: 'center' }}>
          <Spinner size={34} style={{ margin: '4px auto 18px' }} />
          <h1 className="a2-card-title" style={{ fontSize: '1.2rem' }}>Entrando a tu cuenta…</h1>
          <p className="a2-card-sub">Estamos validando tu enlace de acceso.</p>
        </Card>
      ) : (
        <EmptyState
          icon="link_off"
          title="No pudimos abrir tu enlace"
          text={errorMsg}
          action={(
            <Button icon="login" onClick={() => navigate('/login')}>
              Ir a iniciar sesión
            </Button>
          )}
        />
      )}
    </StandaloneScreen>
  );
};

export default Entrar;
